import React, {useState} from "react";
import {ACTIVITY_TYPE} from "../../components/common/Constants";
import {validateEmptyValue, validateMaxLength, validateMinLength} from "./validators";

const validators = [validateEmptyValue, validateMaxLength, validateMinLength];

/**
 *
 * @param {React.Component} WrappedComponent
 * @returns {React.Component}
 */
function withFormGroup(WrappedComponent) {
    /**
     * Form structure and configuration for a form.
     * @param {Object} formGroup
     * @example
     * const formGroup = {
     *   username: {
     *     value: "",
     *     mandatory: true,
     *   },
     *   email: {
     *     value: "",
     *     max: {
     *       value: 15,
     *       message: "Email is required"
     *     },
     *   }
     * }
     *
     * Merges `formGroup` with `initialValues` to set up form control defaults.
     * @param {Object} initialValues
     * const initialValues = {
     *    username: "yourusername",
     *    email: "youremail@yahoo.com"
     * }
     */
    return function WrappedFormGroup(props) {
        let mergedFormGroup = {...props.formGroup};
        let activityType = ACTIVITY_TYPE.CREATE;

        if (props.initialValues) {
            activityType = props.activityType ? props.activityType : ACTIVITY_TYPE.UPDATE;
            mergedFormGroup = Object.keys(props.formGroup).reduce((acc, key) => {
                acc[key] = {
                    ...props.formGroup[key],
                    value:
                        props.initialValues[key] !== undefined
                            ? props.initialValues[key]
                            : props.formGroup[key].value,
                };
                return acc;
            }, {});
        }
        const {
            controls,
            errors,
            reset,
            resetIgnoreKeys,
            validate,
            setValue,
            customValidate,
            setValueNestedObject,
            setInitialValue
        } = useFormGroup(mergedFormGroup);

        return (
            <WrappedComponent
                {...props}
                controls={controls}
                errors={errors}
                activityType={activityType}
                reset={reset}
                resetIgnoreKeys={resetIgnoreKeys}
                validate={validate}
                setValue={setValue}
                customValidate={customValidate}
                setValueNestedObject={setValueNestedObject}
                setInitialValue={setInitialValue}
            />
        );
    };
}

export default withFormGroup;

function useFormGroup(formControls) {
    const [controls, setControls] = useState(formControls);
    const [errors, setErrors] = useState({});

    function validate() {
        const newErrors = {};
        if (controls) {
            for (const [key, data] of Object.entries(controls)) {
                if (data) {
                    for (const validatorFn of validators) {
                        const message = validatorFn(data);
                        if (message) {
                            newErrors[key] = message;
                            continue;
                        }
                    }
                }
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    function reset() {
        setControls((prevControls) =>
            Object.keys(prevControls).reduce((obj, key) => {
                obj[key] = {
                    ...controls[key],
                    value: transformToInitialValue(controls[key].value),
                };
                return obj;
            }, {})
        );
        setErrors({});
    }

    function resetIgnoreKeys(ignoreKeys) {
        setControls((prevControls) =>
            Object.keys(prevControls).reduce((obj, key) => {
                if (ignoreKeys?.includes(key)) {
                    obj[key] = {
                        ...prevControls[key],
                        value: prevControls[key].value,
                    };
                } else {
                    obj[key] = {
                        ...controls[key],
                        value: transformToInitialValue(controls[key].value),
                    };
                }

                return obj;
            }, {})
        );
        setErrors({});
    }

    function setValue(name, value) {
        if (controls[name]) {
            setControls((prevValue) => ({
                ...prevValue,
                [name]: {
                    ...prevValue[name],
                    value: value,
                },
            }));
        }
    }

    function setValueNestedObject(parent, child, value) {
        if (controls[parent]) {
            setControls((prevValue) => ({
                ...prevValue,
                [parent]: {
                    ...prevValue[parent],
                    [child]: {
                        ...prevValue[parent][child],
                        value: value,
                    },
                },
            }));
        }
    }

    function setInitialValue(initialValues) {
        function mergeValues(controlObj, initialValueObj) {
            return Object.keys(controlObj).reduce((acc, key) => {
                if (typeof controlObj[key] === "object" && !Array.isArray(controlObj[key]) && controlObj[key] !== null) {
                    acc[key] = { ...controlObj[key] };
                    if ("value" in acc[key]) {
                        acc[key].value = initialValueObj?.[key] ?? acc[key].value;
                    } else {
                        acc[key] = mergeValues(controlObj[key], initialValueObj?.[key] || {});
                    }
                } else {
                    acc[key] = initialValueObj?.[key] ?? null;
                }
                return acc;
            }, {});
        }

        const parsedInitialValues = mergeValues(controls, initialValues);
        setControls((prevValue) => ({
            ...prevValue,
            ...parsedInitialValues,
        }));
    }

    function customValidate(callback) {
        setErrors(callback);
        return Object.keys(callback).length === 0;
    }

    return {
        errors,
        controls,
        validate,
        resetIgnoreKeys,
        reset,
        setValue,
        customValidate,
        setValueNestedObject,
        setInitialValue,
    };
}

function transformToInitialValue(value) {
    const defaults = {
        boolean: false,
        string: "",
        number: undefined,
        object: Array.isArray(value) ? [] : null,
    };

    return defaults[typeof value] ?? null;
}
